/**
 * Preview de mídia/documentos estilo WhatsApp Web no painel do chat.
 * Overlay absoluto sob o ChatHeader; tokens do DS; reutiliza composer/send.
 */
import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import type { AttachSessionKind } from '../../lib/attachSession';
import { formatFileSize, isPdfFile, readPdfPageCount } from '../../lib/fileMeta';
import Textarea from '../../../../components/ui/Textarea';
import { AppIcon } from '../../../../components/icons/AppIcon';
import { cn } from '@/lib/utils';
import { ActivePreview, AttachStrip } from './ChatMediaPreviewParts';

export interface ChatMediaPreviewItem {
    id: string;
    file: File;
    previewUrl: string;
}

export interface ChatMediaPreviewModalProps {
    isOpen: boolean;
    sessionKind: AttachSessionKind;
    items: ChatMediaPreviewItem[];
    caption: string;
    onCaptionChange: (value: string) => void;
    onRemove: (id: string) => void;
    onAddMore: () => void;
    onClose: () => void;
    onSend: () => void;
}

function resolveSubtitle(
    item: ChatMediaPreviewItem | undefined,
    isMedia: boolean,
    pageCount: number | null
): string {
    if (!item) return '';
    if (!isMedia && pageCount != null) {
        return `${pageCount} ${pageCount === 1 ? 'página' : 'páginas'}`;
    }
    if (!isMedia) return formatFileSize(item.file.size);
    if (item.file.type.startsWith('video/')) return 'Vídeo';
    return 'Imagem';
}

export const ChatMediaPreviewModal: React.FC<ChatMediaPreviewModalProps> = ({
    isOpen,
    sessionKind,
    items,
    caption,
    onCaptionChange,
    onRemove,
    onAddMore,
    onClose,
    onSend,
}) => {
    const [activeIndex, setActiveIndex] = useState(0);
    const [pageCount, setPageCount] = useState<number | null>(null);

    const isMedia = sessionKind === 'media';
    const activeItem = items[activeIndex] ?? items[0];
    const pane = typeof document !== 'undefined' ? document.getElementById('chat-pane-body') : null;

    useEffect(() => {
        if (!isOpen) {
            setActiveIndex(0);
            setPageCount(null);
            return;
        }
        if (activeIndex >= items.length) {
            setActiveIndex(Math.max(0, items.length - 1));
        }
    }, [isOpen, activeIndex, items.length]);

    useEffect(() => {
        let cancelled = false;
        setPageCount(null);
        if (!activeItem || isMedia || !isPdfFile(activeItem.file)) return;
        void readPdfPageCount(activeItem.file).then((count) => {
            if (!cancelled) setPageCount(count);
        });
        return () => {
            cancelled = true;
        };
    }, [activeItem, isMedia]);

    if (!isOpen || !pane) return null;

    const subtitle = resolveSubtitle(activeItem, isMedia, pageCount);

    return createPortal(
        <section
            className="absolute inset-0 z-30 flex flex-col overflow-hidden rounded-none bg-card"
            role="dialog"
            aria-modal="true"
            aria-label={isMedia ? 'Enviar mídia' : 'Enviar documento'}
        >
            <div className="relative flex min-h-0 flex-1 flex-col bg-card">
                <header className="pointer-events-none absolute inset-x-0 top-0 z-10 grid grid-cols-[2.5rem_1fr_2.5rem] items-start gap-2 px-2 pt-2 sm:px-3 sm:pt-3">
                    <button
                        type="button"
                        onClick={onClose}
                        className={cn(
                            'pointer-events-auto flex size-10 items-center justify-center rounded-full',
                            'text-muted transition-colors hover:text-secondary',
                            'focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-primary/20'
                        )}
                        aria-label="Fechar"
                    >
                        <AppIcon name="x" className="size-5" aria-hidden />
                    </button>
                    <div className="min-w-0 pt-1.5 text-center">
                        {!isMedia ? (
                            <>
                                <h2 className="truncate text-body-md font-semibold text-default drop-shadow-sm">
                                    {activeItem?.file.name ?? 'Documento'}
                                </h2>
                                {subtitle ? (
                                    <p className="truncate text-caption text-muted">{subtitle}</p>
                                ) : null}
                            </>
                        ) : null}
                    </div>
                    <span aria-hidden="true" />
                </header>

                <figure
                    className="flex min-h-0 flex-1 items-center justify-center px-3 pb-4 pt-14 sm:px-6 sm:pt-16"
                    aria-label="Pré-visualização"
                >
                    <ActivePreview item={activeItem} isMedia={isMedia} />
                </figure>
            </div>

            <footer className="shrink-0 rounded-none border-t border-subtle bg-card pb-[max(0.5rem,env(safe-area-inset-bottom))]">
                <div className="px-3 pt-3 sm:px-4">
                    <div className="chat-composer min-h-10 rounded-md py-0 focus-within:shadow-none">
                        <label htmlFor="chat-attach-caption" className="sr-only">
                            Legenda
                        </label>
                        <Textarea
                            id="chat-attach-caption"
                            value={caption}
                            onChange={(e) => onCaptionChange(e.target.value)}
                            placeholder="Adicionar legenda..."
                            aria-label="Legenda da mídia"
                            variant="minimal"
                            minRows={1}
                            containerClassName="min-w-0 flex-1"
                            className="chat-composer__field max-h-20 w-full overflow-y-auto text-[16px] md:text-[length:var(--text-base)]"
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' && !e.shiftKey) {
                                    e.preventDefault();
                                    if (items.length > 0) onSend();
                                }
                            }}
                        />
                    </div>
                </div>

                <nav
                    aria-label={isMedia ? 'Arquivos de mídia' : 'Documentos'}
                    className="flex items-end gap-3 px-3 py-3 sm:px-4"
                >
                    <AttachStrip
                        items={items}
                        isMedia={isMedia}
                        activeIndex={activeIndex}
                        onSelect={setActiveIndex}
                        onRemove={onRemove}
                        onAddMore={onAddMore}
                    />
                    <button
                        type="button"
                        onClick={onSend}
                        disabled={items.length === 0}
                        className="chat-composer__icon chat-composer__icon--send size-12 shrink-0 disabled:opacity-50"
                        title="Enviar"
                        aria-label="Enviar"
                    >
                        <AppIcon
                            name="send"
                            className="size-[18px] rotate-45 text-primary-foreground"
                            aria-hidden
                        />
                    </button>
                </nav>
            </footer>
        </section>,
        pane
    );
};

export default ChatMediaPreviewModal;
